import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * PortfolioSummaryCards Component
 * 
 * Displays 4 key portfolio summary metrics:
 * 1. Total Stocks - Number of stocks in portfolio
 * 2. Buy Now - Stocks ready to buy immediately
 * 3. Watchlist - Stocks waiting for better entry
 * 4. Cash Reserved - Percentage held in cash
 * 
 * @param {Object} portfolio - Portfolio data from API
 */
const PortfolioSummaryCards = ({ portfolio }) => {
  const { t } = useTranslation();

  if (!portfolio) return null;

  // Calculate metrics from actual data
  const portfolioStocks = portfolio.portfolio || [];
  const watchlistStocks = portfolio.watchlist || [];
  const buyNowStocks = portfolioStocks.filter(s => s.state === 'BUY_NOW');
  const waitStocks = watchlistStocks.filter(s => s.state === 'WAIT');
  
  // Calculate total allocated weight from portfolio
  const totalAllocated = portfolioStocks.reduce((sum, s) => sum + (s.weight || 0), 0);
  const cashReserved = Math.max(0, 1 - totalAllocated); // Cash = 1 - allocated

  const summaryMetrics = [
    {
      key: 'totalStocks',
      label: t('riskManagement.portfolioConstruction.summary.totalStocks'),
      value: portfolio.summary?.total_stocks || portfolioStocks.length,
      description: t('riskManagement.portfolioConstruction.summary.inPortfolio'),
      color: 'text.primary',
      borderColor: 'divider'
    },
    {
      key: 'buyNow',
      label: t('riskManagement.portfolioConstruction.summary.buyNow'),
      value: portfolio.summary?.buy_now_count || buyNowStocks.length,
      description: t('riskManagement.portfolioConstruction.summary.readyToBuy'),
      color: '#4caf50',
      borderColor: '#4caf50'
    },
    {
      key: 'watchlist',
      label: t('riskManagement.portfolioConstruction.summary.watchlist'),
      value: portfolio.summary?.wait_pullback_count || waitStocks.length,
      description: t('riskManagement.portfolioConstruction.summary.waitForEntry'),
      color: '#ff9800',
      borderColor: '#ff9800'
    },
    {
      key: 'cashReserved',
      label: t('riskManagement.portfolioConstruction.summary.cashReserved'),
      value: `${((portfolio.summary?.cash_reserved || cashReserved) * 100).toFixed(0)}%`,
      description: t('riskManagement.portfolioConstruction.summary.forPullbacks'),
      color: '#2196f3',
      borderColor: '#2196f3'
    }
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
      {summaryMetrics.map((metric) => (
        <Box key={metric.key}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              display: 'block',
              mb: 1,
              fontSize: '0.65rem',
              letterSpacing: '0.5px',
            }}
          >
            {metric.label}
          </Typography>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: metric.color,
              mb: 0.5,
              lineHeight: 1,
            }}
          >
            {metric.value}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.7rem',
              display: 'block',
            }}
          >
            {metric.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default PortfolioSummaryCards;
